import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  TextField,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  Modal,
  MessageBar,
  MessageBarType,
  Toggle,
  Label,
  IconButton,
  Image,
  ImageFit,
  Panel,
  PanelType,
  Spinner,
  SpinnerSize,
  TagPicker,
  ITag
} from '@fluentui/react';
import {
  AddRegular,
  EditRegular,
  DeleteRegular,
  SaveRegular,
  DismissRegular,
  ImageRegular
} from '@fluentui/react-icons';
import styles from './NewsEditor.module.css';
import { authenticatedFetch } from '../../utils/authApi';

interface NewsArticle {
  id?: string;
  title: string;
  summary: string;
  content: string;
  category: string;
  author: string;
  tags: string[];
  featured_image?: string;
  additional_images: string[];
  is_featured: boolean;
  publish_date: string;
}

interface NewsEditorProps {
  isOpen: boolean;
  onDismiss: () => void;
  article?: NewsArticle | null;
  onSave: () => void;
}

const NewsEditor: React.FC<NewsEditorProps> = ({
  isOpen,
  onDismiss,
  article,
  onSave
}) => {
  const [formData, setFormData] = useState<NewsArticle>({
    title: '',
    summary: '',
    content: '',
    category: '',
    author: '',
    tags: [],
    additional_images: [],
    is_featured: false,
    publish_date: new Date().toISOString().split('T')[0]
  });

  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      if (article) {
        setFormData({
          ...article,
          publish_date: article.publish_date.split('T')[0]
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, article]);

  const loadCategories = async () => {
    try {
      const response = await authenticatedFetch('/api/news/categories');
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories.filter((cat: string) => cat !== 'all'));
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      summary: '',
      content: '',
      category: '',
      author: '',
      tags: [],
      additional_images: [],
      is_featured: false,
      publish_date: new Date().toISOString().split('T')[0]
    });
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (field: keyof NewsArticle, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (file: File, isFeatureImage: boolean = false) => {
    try {
      setImageUploadLoading(true);
      
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target?.result as string;
        
        try {
          const response = await authenticatedFetch('/api/news/upload-image', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              image: base64Data,
              filename: file.name
            })
          });
          
          const data = await response.json();
          
          if (data.success) {
            if (isFeatureImage) {
              handleInputChange('featured_image', data.image_url);
            } else {
              handleInputChange('additional_images', [
                ...formData.additional_images,
                data.image_url
              ]);
            }
            setSuccess('Image uploaded successfully!');
          } else {
            setError(data.error || 'Failed to upload image');
          }
        } catch (error) {
          setError('Failed to upload image');
        } finally {
          setImageUploadLoading(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      setError('Failed to process image');
      setImageUploadLoading(false);
    }
  };

  const handleFileSelect = (isFeatureImage: boolean = false) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleImageUpload(file, isFeatureImage);
      }
    };
    input.click();
  };

  const removeAdditionalImage = (index: number) => {
    const newImages = formData.additional_images.filter((_, i) => i !== index);
    handleInputChange('additional_images', newImages);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate required fields
      if (!formData.title || !formData.summary || !formData.content || !formData.category) {
        setError('Please fill in all required fields');
        return;
      }

      const url = article?.id ? `/api/news/${article.id}` : '/api/news';
      const method = article?.id ? 'PUT' : 'POST';

      const response = await authenticatedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          publish_date: new Date(formData.publish_date).toISOString()
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(article?.id ? 'Article updated successfully!' : 'Article created successfully!');
        setTimeout(() => {
          onSave();
          onDismiss();
        }, 1500);
      } else {
        setError(data.error || 'Failed to save article');
      }
    } catch (error) {
      setError('Failed to save article');
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions: IDropdownOption[] = [
    ...categories.map(cat => ({
      key: cat,
      text: cat.charAt(0).toUpperCase() + cat.slice(1)
    })),
    { key: 'new', text: '+ Add New Category' }
  ];

  const tagSuggestions: ITag[] = [
    { key: 'feature', name: 'Feature' },
    { key: 'update', name: 'Update' },
    { key: 'tutorial', name: 'Tutorial' },
    { key: 'release', name: 'Release' },
    { key: 'bugfix', name: 'Bug Fix' },
    { key: 'enhancement', name: 'Enhancement' },
    { key: 'announcement', name: 'Announcement' }
  ];

  const selectedTags: ITag[] = formData.tags.map(tag => ({ key: tag, name: tag }));

  const onTagsChange = (items?: ITag[]) => {
    const tags = items ? items.map(item => item.name) : [];
    handleInputChange('tags', tags);
  };

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.large}
      headerText={article?.id ? 'Edit Article' : 'Create New Article'}
      closeButtonAriaLabel="Close"
      className={styles.editorPanel}
    >
      <div className={styles.editorContainer}>
        {error && (
          <MessageBar
            messageBarType={MessageBarType.error}
            onDismiss={() => setError(null)}
            className={styles.messageBar}
          >
            {error}
          </MessageBar>
        )}

        {success && (
          <MessageBar
            messageBarType={MessageBarType.success}
            onDismiss={() => setSuccess(null)}
            className={styles.messageBar}
          >
            {success}
          </MessageBar>
        )}

        <Stack tokens={{ childrenGap: 24 }}>
          {/* Basic Information */}
          <div className={styles.section}>
            <Text variant="large" className={styles.sectionTitle}>
              Basic Information
            </Text>
            
            <Stack tokens={{ childrenGap: 16 }}>
              <TextField
                label="Title *"
                value={formData.title}
                onChange={(_, value) => handleInputChange('title', value || '')}
                placeholder="Enter article title"
                required
              />

              <TextField
                label="Summary *"
                value={formData.summary}
                onChange={(_, value) => handleInputChange('summary', value || '')}
                placeholder="Brief summary of the article"
                multiline
                rows={3}
                required
              />

              <Stack horizontal tokens={{ childrenGap: 16 }}>
                <Dropdown
                  label="Category *"
                  placeholder="Select category"
                  options={categoryOptions}
                  selectedKey={formData.category}
                  onChange={(_, option) => {
                    if (option?.key === 'new') {
                      // Handle new category creation
                      const newCategory = prompt('Enter new category name:');
                      if (newCategory) {
                        setCategories(prev => [...prev, newCategory]);
                        handleInputChange('category', newCategory);
                      }
                    } else {
                      handleInputChange('category', option?.key as string);
                    }
                  }}
                  className={styles.dropdown}
                  required
                />

                <TextField
                  label="Publish Date"
                  type="date"
                  value={formData.publish_date}
                  onChange={(_, value) => handleInputChange('publish_date', value || '')}
                  className={styles.dateField}
                />
              </Stack>

              <TagPicker
                onResolveSuggestions={(filterText) =>
                  tagSuggestions.filter(tag =>
                    tag.name.toLowerCase().includes(filterText.toLowerCase())
                  )
                }
                getTextFromItem={(item) => item.name}
                pickerSuggestionsProps={{
                  suggestionsHeaderText: 'Suggested tags',
                  noResultsFoundText: 'No tags found'
                }}
                itemLimit={10}
                selectedItems={selectedTags}
                onChange={onTagsChange}
                inputProps={{
                  placeholder: 'Add tags...'
                }}
              />

              <Toggle
                label="Featured Article"
                checked={formData.is_featured}
                onChange={(_, checked) => handleInputChange('is_featured', checked || false)}
                inlineLabel
              />
            </Stack>
          </div>

          {/* Content Editor */}
          <div className={styles.section}>
            <Text variant="large" className={styles.sectionTitle}>
              Content
            </Text>
            
            <TextField
              label="Content *"
              value={formData.content}
              onChange={(_, value) => handleInputChange('content', value || '')}
              placeholder="Enter article content (supports HTML)"
              multiline
              rows={15}
              className={styles.contentEditor}
              required
            />
            
            <Text variant="small" className={styles.helpText}>
              You can use HTML tags for formatting (h1, h2, p, ul, ol, li, strong, em, etc.)
            </Text>
          </div>

          {/* Image Management */}
          <div className={styles.section}>
            <Text variant="large" className={styles.sectionTitle}>
              Images
            </Text>
            
            {/* Featured Image */}
            <div className={styles.imageSection}>
              <Label>Featured Image</Label>
              
              {formData.featured_image ? (
                <div className={styles.imagePreview}>
                  <Image
                    src={formData.featured_image}
                    alt="Featured image"
                    imageFit={ImageFit.cover}
                    className={styles.previewImage}
                  />
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => handleInputChange('featured_image', undefined)}
                    className={styles.removeImageButton}
                    title="Remove image"
                  />
                </div>
              ) : (
                <div 
                  className={styles.uploadArea}
                  onClick={() => handleFileSelect(true)}
                >
                  <ImageRegular className={styles.uploadIcon} />
                  <Text>Click to upload featured image</Text>
                </div>
              )}
            </div>

            {/* Additional Images */}
            <div className={styles.imageSection}>
              <Label>Additional Images</Label>
              
              <div className={styles.additionalImages}>
                {formData.additional_images.map((imageUrl, index) => (
                  <div key={index} className={styles.imagePreview}>
                    <Image
                      src={imageUrl}
                      alt={`Additional image ${index + 1}`}
                      imageFit={ImageFit.cover}
                      className={styles.previewImage}
                    />
                    <IconButton
                      iconProps={{ iconName: 'Delete' }}
                      onClick={() => removeAdditionalImage(index)}
                      className={styles.removeImageButton}
                      title="Remove image"
                    />
                  </div>
                ))}
                
                <div 
                  className={styles.uploadArea}
                  onClick={() => handleFileSelect(false)}
                >
                  <AddRegular className={styles.uploadIcon} />
                  <Text>Add image</Text>
                </div>
              </div>
            </div>

            {imageUploadLoading && (
              <div className={styles.uploadingIndicator}>
                <Spinner size={SpinnerSize.small} />
                <Text variant="small">Uploading image...</Text>
              </div>
            )}
          </div>
        </Stack>

        {/* Action Buttons */}
        <div className={styles.actionButtons}>
          <Stack horizontal tokens={{ childrenGap: 12 }} horizontalAlign="end">
            <DefaultButton
              text="Cancel"
              onClick={onDismiss}
              disabled={loading}
            />
            <PrimaryButton
              text={article?.id ? 'Update Article' : 'Create Article'}
              onClick={handleSave}
              disabled={loading || !formData.title || !formData.summary || !formData.content || !formData.category}
              iconProps={loading ? undefined : { iconName: 'Save' }}
            >
              {loading && <Spinner size={SpinnerSize.xSmall} />}
            </PrimaryButton>
          </Stack>
        </div>
      </div>
    </Panel>
  );
};

export default NewsEditor;
